import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Box, Typography, styled } from '@material-ui/core';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { EligibilityFormCustomization } from '../../Onboarding/OnboardingFlowContext';
import { SleepioWellnessOnboardingParams } from '../SleepioWellnessOnboardingPage';
import { Loader } from '../components';
import { SleepioWellnessOnboardingStep } from '../sleepioWellnessOnboardingState';

import sparkTheme from '@/themes/sparkTheme';
import { Button, TextField } from '@/components';
import { formatDateInput } from '@/utils/inputHelpers';
import { SET_EMAIL_VERIFIED, SAVE_SLEEPIO_COVERAGE_DETAILS } from '@/apollo/mutations';
import { CHECK_ELIGIBILITY_QUERY } from '@/apollo/queries';
import { parseSearchParams, formatDateString } from '@/utils/stringUtils';
import { getPdtUid, getProductId, isSpark as isSparkHelper } from '@/utils/productHelpers';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { getProgram } from '@/utils/posthog';
import { useUserUid } from '@/hooks/useUserUid';
import { useUtmParams } from '@/hooks/useUtmParams';

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginTop: 8,
  '& > *': {
    marginTop: 24,
  },
});

type Props = {
  onNext: (step: SleepioWellnessOnboardingStep) => void;
  onError: () => void;
  formCustomization: EligibilityFormCustomization;
};

const ConfirmDetails = ({ onNext, formCustomization, onError }: Props) => {
  const { t } = useTranslation();
  const {
    careTeamUid,
    token,
    slug,
    product,
  } = useParams<SleepioWellnessOnboardingParams>();
  // Save the arrival timestamp when the component mounts
  const arrivalTimestampRef = useRef<string>(new Date().toISOString());

  const posthogProgram = getProgram(product);
  const posthog = usePostHog();
  const { userUid, setUserUid } = useUserUid();
  const { utmParams } = useUtmParams();
  const isSpark = isSparkHelper(product);
  const posthogCoverageFailed = () => {
    if (isSpark) {
      posthog.capture('CoverageCheckFailed', {
        payer_name: slug,
        'wellness.cortex_uid': userUid,
        product_line: posthogProgram,
      });
    }
  };

  const {
    showZipCode, showEmployeeId, employeeIdFieldRename, employeeIdExample, employeeIdValidationEnabled,
  } = formCustomization;
  const employeeIdField = employeeIdFieldRename
    || t('coverage.sleepio.patient.employee_id_label');

  const generatePlaceHolder = useCallback((label: string) => (
    `${t('coverage.enter')} ${label.toLocaleLowerCase()}...`
  ), []);

  const location = useLocation();

  const { formState: { errors, isValid }, clearErrors, getValues } = useFormContext();

  const [forceEligibilitySuccess, setForceEligibilitySuccess] = useState(false);
  const [forceError, setForceError] = useState(false);
  const [forceEligibilityFailure, setForceEligibilityFailure] = useState(false);

  useEffect(() => {
    const searchParams = parseSearchParams(location.search);
    if (searchParams?.forceError === 'true') {
      setForceError(true);
    }
  }, [location.search]);

  const [verificationComplete, setVerificationComplete] = useState(false);

  /** Verify email */
  const [setEmailVerified, { loading: emailVerificationLoading }] = useMutation(SET_EMAIL_VERIFIED);
  const handleVerifyEmail = useCallback(async (error?: boolean) => {
    if (error && process.env.NODE_ENV !== 'production') {
      // TODO(SD-970): show actual error page?
      throw new Error('Override error');
    }

    try {
      const response = await setEmailVerified({
        variables: {
          careTeamUid,
          token,
        },
      });

      const result = response?.data?.setEmailVerified;

      if (result?.success) {
        setUserUid(result.userUid);
        setVerificationComplete(true);

        posthog.identify(result.distinctId);

        if (isSpark) {
          posthog.capture('CoverageCheckStarted', {
            program: getProgram(product),
            'wellness.cortex_id': result.userUid,
          });
        }
      } else {
        console.error('Error verifying email: ', response?.errors);
        onError();
      }
    } catch (e) {
      console.error(e);
    }
  }, [forceError, setVerificationComplete, setEmailVerified]);

  useEffect(() => {
    if (!verificationComplete) {
      handleVerifyEmail(forceError);
    }
  }, [verificationComplete, forceError]);

  /** Check coverage eligibility */
  const [checkEligibility, { loading: eligibilityCheckLoading }] = useLazyQuery(CHECK_ELIGIBILITY_QUERY);
  const checkCoverage = useCallback(async (): Promise<boolean> => {
    let eligibilityPass = false;

    if (forceEligibilitySuccess) {
      onNext('CreatePassword');
      eligibilityPass = true;
    }
    if (forceEligibilityFailure) {
      posthogCoverageFailed();
      onNext('CoverageRejection');
    }

    try {
      const coverageData = getValues();
      // format as YYYY-MM-DD for GraphQL Date scalar type;
      const convertedDateOfBirth = formatDateString(coverageData.dateOfBirth);

      const result = await checkEligibility({
        variables: {
          uid: userUid,
          pdtUid: getPdtUid(product),
          firstName: coverageData.firstName,
          lastName: coverageData.lastName,
          dateOfBirth: convertedDateOfBirth,
          postalCode: showZipCode ? coverageData.zipCode : undefined,
          organizationSlug: slug,
          employeeId: showEmployeeId ? coverageData.employeeId : undefined,
          productId: getProductId(product),
          ...utmParams,
        },
      });
      if (isSpark) {
        posthog.capture('CoverageCheckSubmitted', {
          program: posthogProgram,
          first_name: coverageData.firstName,
          last_name: coverageData.lastName,
          zipcode: coverageData.zipCode,
          birth_date: convertedDateOfBirth,
          external_id: coverageData.employeeId,
          'wellness.cortex_id': userUid,
        });
      }

      if (result?.data?.eligibilityStatus.isEligible) {
        eligibilityPass = true;
      } else if (result?.data?.eligibilityStatus.isEligible === false) {
        posthogCoverageFailed();
        console.warn('Coverage check failed ', result.error);
        eligibilityPass = false;
      } else {
        // TODO(SD-970): render generic error state? or maybe just
        // error text at the bottom of the form like we do in the current flow
        posthogCoverageFailed();
        console.warn('Unable to determine whether the user is eligible.');
      }
    } catch (e) {
      console.error(e);
      posthogCoverageFailed();
    }

    return eligibilityPass;
  }, [forceEligibilitySuccess, forceEligibilityFailure, userUid]);

  /** Save coverage details */
  const [saveCoverageDetails, { loading: saveCoverageLoading }] = useMutation(SAVE_SLEEPIO_COVERAGE_DETAILS);
  const updateUserProfile = useCallback(async (eligibilityStatus: string) => {
    try {
      const coverageData = getValues();

      const response = await saveCoverageDetails({
        variables: {
          careTeamUid,
          token,
          // Format as YYYY-MM-DD for GraphQL Date scalar type
          dateOfBirth: formatDateString(coverageData.dateOfBirth),
          firstName: coverageData.firstName,
          lastName: coverageData.lastName,
          postalCode: showZipCode ? coverageData.zipCode : undefined,
          coverageCheckStartTimestamp: arrivalTimestampRef.current,
          coverageCheckTimestamp: new Date().toISOString(),
          coverageCheckStatus: eligibilityStatus,
          employeeId: showEmployeeId ? coverageData.employeeId : undefined,
        },
      });

      const saveCoverageResult = response?.data?.saveCoverageDetails;
      if (saveCoverageResult?.success) {
        posthog.capture('ProfileUpdated', {
          program: posthogProgram,
          'wellness.cortex_id': saveCoverageResult?.userUid,
        });
      }
    } catch (e) {
      // TODO(SD-970): render error state
      console.error(`Coverage details not saved: ${e}`);
      posthog.capture('ActivationFlowErrored', {
        program: posthogProgram,
      });
    }
  }, []);

  if (emailVerificationLoading) {
    return <Loader />;
  }

  return (
    <ContentContainer>
      <Box>
        <Typography variant="h2" style={{ marginBottom: 8 }}>
          {
            formCustomization.isGeneric
              ? t('coverage.sleepio.patient.confirm_details.generic_title')
              : t('coverage.sleepio.patient.confirm_details.title')
          }
        </Typography>
        <Typography>
          {
            formCustomization.isGeneric
              ? t('coverage.sleepio.patient.confirm_details.generic_subtitle')
              : t('coverage.sleepio.patient.confirm_details.subtitle')
          }
        </Typography>
      </Box>
      <Controller
        name="firstName"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="first-name-input"
            required
            label={t('coverage.sleepio.patient.f_name_label')}
            placeholder={generatePlaceHolder(t('coverage.sleepio.patient.f_name_label'))}
            helperText={errors?.firstName?.message}
            error={!!errors?.firstName}
            onFocus={() => clearErrors('firstName')}
          />
        )}
      />
      <Controller
        name="lastName"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="last-name-input"
            required
            label={t('coverage.sleepio.patient.l_name_label')}
            placeholder={generatePlaceHolder(t('coverage.sleepio.patient.l_name_label'))}
            helperText={errors?.lastName?.message}
            error={!!errors?.lastName}
            onFocus={() => clearErrors('lastName')}
          />
        )}
      />
      {showZipCode && (
        <Controller
          name="zipCode"
          render={({ field }) => (
            <TextField
              {...field}
              required
              data-testid="zip-code-input"
              id="confimDetailsFormZipCode"
              label={t('coverage.sleepio.patient.zipcode_label')}
              placeholder={generatePlaceHolder(t('coverage.sleepio.patient.zipcode_label'))}
              helperText={errors?.zipCode?.message}
              error={!!errors?.zipCode}
              onFocus={() => clearErrors('zipCode')}
              inputProps={{
                maxLength: 5,
                inputMode: 'numeric',
              }}
            />
          )}
        />
      )}
      {showEmployeeId && (
        <Controller
          name="employeeId"
          render={({ field }) => (
            <TextField
              {...field}
              required
              data-testid="employee-id-input"
              label={employeeIdField}
              placeholder={
                (employeeIdValidationEnabled && employeeIdExample)
                  ? employeeIdExample
                  : generatePlaceHolder(employeeIdField)
              }
              helperText={errors?.employeeId?.message}
              error={!!errors?.employeeId}
              onFocus={() => clearErrors('employeeId')}
            />
          )}
        />
      )}
      <Controller
        name="dateOfBirth"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="dob-input"
            required
            label={t('coverage.sleepio.patient.dob_label')}
            placeholder="MM / DD / YYYY"
            helperText={errors?.dateOfBirth?.message}
            error={!!errors?.dateOfBirth}
            onFocus={() => clearErrors('dateOfBirth')}
            inputProps={{
              maxLength: 14,
              inputMode: 'numeric',
            }}
            onChange={(e) => {
              const value = formatDateInput(e.target.value);
              field.onChange(value);
            }}
          />
        )}
      />
      <Button
        role="button"
        variant="contained"
        data-testid="submit-button"
        id="confirmDetailsFormSubmitButton"
        type="submit"
        fullWidth
        color="primary"
        disabled={!isValid}
        rounded
        loading={(eligibilityCheckLoading || saveCoverageLoading)}
        style={
          isSpark
            ? {
              borderRadius: 100,
              ...(isValid && !(eligibilityCheckLoading || saveCoverageLoading)
                ? {
                  backgroundColor: sparkTheme.palette.primary.main,
                  color: sparkTheme.palette.getContrastText(
                    sparkTheme.palette.primary.main,
                  ),
                }
                : {}),
            }
            : undefined
        }
        onClick={async () => {
          try {
            const coverageCheckSuccess = await checkCoverage();
            const coverageCheckStatus = coverageCheckSuccess ? 'pass' : 'fail';
            await updateUserProfile(coverageCheckStatus);
            if (isSpark) {
              posthog.capture('CoverageCheckCompleted', {
                program: posthogProgram,
                coverage_check_status: coverageCheckStatus,
                'wellness.cortex_id': userUid,
              });
            }
            if (coverageCheckSuccess) {
              onNext('CreatePassword');
            } else {
              onNext('CoverageRejection');
            }
          } catch (e) {
            // TODO(970): render error state
            console.error(e);
          }
        }}
      >
        {formCustomization.isGeneric ? t('coverage.continue') : t('coverage.claim_benefit')}
      </Button>
      {process.env.NODE_ENV !== 'production' && (
        <>
          <OverrideToggle
            userMessage="Force Eligibility Success"
            override={setForceEligibilitySuccess}
          />
          <OverrideToggle
            userMessage="Force Eligibility Failure"
            override={setForceEligibilityFailure}
          />
        </>
      )}
    </ContentContainer>
  );
};

export default ConfirmDetails;
